import React from 'react';
import { Group, Rect, Circle, Text } from 'react-konva';
import { TableItem as TableItemType, Position, Size } from '../../types';

// Conversion factor for feet to pixels (1 foot = 10 pixels)
const FEET_TO_PIXELS = 10;

// Chair dimensions in feet
const CHAIR_SIZE = 1.5 * FEET_TO_PIXELS;
// Total space from table edge to end of chair should be 2.0 feet (reduced from 2.5 feet)
// Since chair size is 1.5 feet, the spacing should be 2.0 - 1.5/2 = 1.25 feet
// (subtracting half chair size because we measure from table edge to chair center, and need to account for chair radius)
const CHAIR_SPACING = 1.25 * FEET_TO_PIXELS; // Space between table edge and chair center

interface TableItemProps {
  id: string;
  item: TableItemType;
  position: Position;
  size: Size;
  rotation: number;
  isSelected: boolean;
  onDragStart: (e: any) => void;
  onDragEnd: (e: any) => void;
  onClick: (e: any) => void;
  onRotate: (id: string, rotation: number) => void;
}

const TableItem: React.FC<TableItemProps> = ({
  id,
  item,
  position,
  size,
  rotation,
  isSelected,
  onDragStart,
  onDragEnd,
  onClick,
  onRotate
}) => {
  const { shape, seats, label } = item;

  const renderSeats = () => {
    if (shape === 'circle') {
      // For round tables, distribute chairs evenly around the circle
      const tableRadius = size.width / 2; // Since width=height for circle
      const chairRadius = CHAIR_SIZE / 2;
      const chairDistance = tableRadius + chairRadius + CHAIR_SPACING;
      
      return Array.from({ length: seats }).map((_, index) => {
        const angle = (index * 2 * Math.PI) / seats;
        const x = Math.cos(angle) * chairDistance;
        const y = Math.sin(angle) * chairDistance;
        
        return (
          <Circle
            key={index}
            x={x}
            y={y}
            radius={chairRadius}
            fill="#f0f0f0"
            stroke="#888"
            strokeWidth={1}
          />
        );
      });
    } else {
      // For rectangular tables
      const tableWidth = size.width;
      const tableHeight = size.height;
      const chairRadius = CHAIR_SIZE / 2;
      
      // Special case for head tables with chairs on one side only
      if (label?.toLowerCase().includes('head table')) {
        const chairSpacing = tableWidth / (seats + 1);
        
        return Array.from({ length: seats }).map((_, index) => {
          // Position along the bottom side of the table
          const xPos = -tableWidth / 2 + chairSpacing * (index + 1);
          // Position on bottom of the table only
          const yPos = tableHeight / 2 + chairRadius + CHAIR_SPACING;
          
          return (
            <Circle
              key={index}
              x={xPos}
              y={yPos}
              radius={chairRadius}
              fill="#f0f0f0"
              stroke="#888"
              strokeWidth={1}
            />
          );
        });
      }
      
      // Standard rectangular table with chairs on both sides
      const chairsPerSide = seats / 2;
      const chairSpacing = tableWidth / (chairsPerSide + 1);
      
      return Array.from({ length: seats }).map((_, index) => {
        const isTopRow = index < chairsPerSide;
        
        // Calculate spacing between chairs
        const rowIndex = isTopRow ? index : index - chairsPerSide;
        
        // Position along the long side of the table
        const xPos = -tableWidth / 2 + chairSpacing * (rowIndex + 1);
        
        // Position on top or bottom of the table
        const yPos = isTopRow 
          ? -tableHeight / 2 - chairRadius - CHAIR_SPACING // Top row
          : tableHeight / 2 + chairRadius + CHAIR_SPACING; // Bottom row
          
        return (
          <Circle
            key={index}
            x={xPos}
            y={yPos}
            radius={chairRadius}
            fill="#f0f0f0"
            stroke="#888"
            strokeWidth={1}
          />
        );
      });
    }
  };

  // Handle rotation with the rotation handle
  const handleRotateStart = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
  };
  
  const handleRotateMove = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
    
    // Calculate the center of the table
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    
    // Use the group's absolute position for calculations
    const groupNode = e.target.getParent();
    const groupAbsPos = groupNode.getAbsolutePosition();
    
    // Calculate angle between center of table and current mouse position
    const deltaX = pointerPos.x - groupAbsPos.x;
    const deltaY = pointerPos.y - groupAbsPos.y;
    
    // Calculate angle in degrees and add 90° to make rotation start from the top
    let angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI + 90;
    
    // Normalize angle to 0-360 range
    if (angle < 0) angle += 360;
    
    // Round to nearest 15 degrees for better usability
    const snappedAngle = Math.round(angle / 15) * 15;
    
    // Apply rotation
    onRotate(id, snappedAngle);
  };

  return (
    <Group
      id={id}
      x={position.x}
      y={position.y}
      rotation={rotation}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {/* Table Shape */}
      {shape === 'rectangle' ? (
        <Rect
          id={id}
          width={size.width}
          height={size.height}
          fill="#d4d4d4"
          stroke={isSelected ? "#3498db" : "#888"}
          strokeWidth={isSelected ? 2 : 1}
          cornerRadius={5}
          x={-size.width / 2}
          y={-size.height / 2}
        />
      ) : (
        <Circle
          id={id}
          radius={size.width / 2}
          fill="#d4d4d4"
          stroke={isSelected ? "#3498db" : "#888"}
          strokeWidth={isSelected ? 2 : 1}
        />
      )}

      {/* Table Label */}
      {label && (
        <Text
          text={label}
          fontSize={12}
          fill="#333"
          align="center"
          verticalAlign="middle"
          width={size.width * 0.8}
          height={size.height * 0.8}
          offsetX={size.width * 0.4}
          offsetY={size.height * 0.4}
        />
      )}

      {/* Chairs */}
      {renderSeats()}

      {/* Rotation Handle - only show when selected */}
      {isSelected && (
        <>
          {/* Rotation indicator line */}
          <Rect
            x={0}
            y={-size.height / 2 - 30}
            width={2}
            height={30}
            fill="#3498db"
            offsetX={1}
          />
          
          {/* Rotation handle */}
          <Circle
            x={0}
            y={-size.height / 2 - 40}
            radius={10}
            fill="#3498db"
            stroke="#fff"
            strokeWidth={2}
            draggable
            onDragStart={handleRotateStart}
            onDragMove={handleRotateMove}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'grab';
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'default';
            }}
          />
        </>
      )}
    </Group>
  );
};

export default TableItem; 